import { useEffect, useId, useRef } from 'react';

type Point = { x: number; y: number; z?: number };
type Vertex = [number, number, number];
type Face = [number, number, number];
type Mesh = {
  points: Point[];
  faces: Face[];
  // A zero amplitude pins a vertex, including the entire pipeline silhouette.
  amplitudes: number[];
  seed: number;
};

const amplitudeScale = 1.1;
const motionSpeed = 1.15;

// Stable per-vertex variation, never frame-by-frame randomness. Two slow waves
// per axis give every junction its own bounded, smoothly changing direction.
const variation = (seed: number) => {
  const n = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return n - Math.floor(n);
};
const driftProfile = (seed: number) => ({
  rate: 0.25 + variation(seed) * 0.18,
  secondaryRate: 0.1 + variation(seed + 1) * 0.12,
  phase: variation(seed + 2) * Math.PI * 2,
  secondaryPhase: variation(seed + 3) * Math.PI * 2,
});
const driftAt = (time: number, profile: ReturnType<typeof driftProfile>) =>
  0.68 * Math.sin(time * profile.rate + profile.phase)
  + 0.32 * Math.sin(time * profile.secondaryRate + profile.secondaryPhase);

const polygon = (points: Point[], face: Face) =>
  face.map(index => `${points[index].x.toFixed(2)},${points[index].y.toFixed(2)}`).join(' ');

function edgesFor(faces: Face[]) {
  const edges = new Map<string, [number, number]>();
  for (const face of faces) {
    for (let i = 0; i < 3; i++) {
      const a = face[i], b = face[(i + 1) % 3];
      edges.set([a, b].sort((x, y) => x - y).join('-'), [a, b]);
    }
  }
  return [...edges.values()];
}

const edgePath = (points: Point[], edges: [number, number][]) => edges.map(([a, b]) =>
  `M${points[a].x.toFixed(2)} ${points[a].y.toFixed(2)}L${points[b].x.toFixed(2)} ${points[b].y.toFixed(2)}`,
).join('');

// Small, connected vertex displacements only: no rotation or moving texture.
// Each face shares the same points, so the solid never tears at its seams.
function AmbientMesh({ mesh, pipeline = false }: { mesh: Mesh; pipeline?: boolean }) {
  const groupRef = useRef<SVGGElement>(null);
  const id = useId().replace(/:/g, '');
  const edges = edgesFor(mesh.faces);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    const svg = group.ownerSVGElement;
    if (!svg) return;
    const polygons = [...group.querySelectorAll<SVGPolygonElement>('.ambient-plane')];
    const paths = [...group.querySelectorAll<SVGPathElement>('.ambient-edges')];
    const glints = [...group.querySelectorAll<SVGCircleElement>('[data-vertex]')];
    const meshEdges = edgesFor(mesh.faces);
    const trajectories = mesh.points.map((_, i) => ({
      x: driftProfile(mesh.seed + i * 17),
      y: driftProfile(mesh.seed + i * 17 + 7),
    }));
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let frame = 0;
    let previous = 0;
    let elapsed = 0;

    const draw = (time: number) => {
      // Start from the static drawing with zero initial velocity. Drifts stay
      // within the amplitude, rather than migrating away from the silhouette.
      const settle = 1 - Math.exp(-Math.pow(time * 0.4, 2));
      const points = mesh.points.map((point, i) => {
        const amplitude = mesh.amplitudes[i] * amplitudeScale * settle;
        return {
          x: point.x + amplitude * driftAt(time, trajectories[i].x),
          y: point.y + amplitude * driftAt(time, trajectories[i].y),
        };
      });
      polygons.forEach((element, i) => element.setAttribute('points', polygon(points, mesh.faces[i])));
      const d = edgePath(points, meshEdges);
      paths.forEach(element => element.setAttribute('d', d));
      glints.forEach(element => {
        const point = points[Number(element.dataset.vertex)];
        element.setAttribute('cx', point.x.toFixed(2));
        element.setAttribute('cy', point.y.toFixed(2));
      });
    };

    const tick = (now: number) => {
      frame = 0;
      if (!visible || document.hidden || motion.matches) return;
      // This slow motion needs at most 30 redraws/sec. No React rerenders.
      if (!previous) previous = now;
      if (now - previous >= 1000 / 30) {
        elapsed += Math.min((now - previous) / 1000, 0.1) * motionSpeed;
        previous = now;
        draw(elapsed);
      }
      frame = requestAnimationFrame(tick);
    };
    const resume = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      previous = 0;
      if (motion.matches) { elapsed = 0; draw(0); }
      else if (visible && !document.hidden) frame = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      resume();
    });
    observer.observe(svg);
    motion.addEventListener('change', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      motion.removeEventListener('change', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [mesh]);

  return (
    <g ref={groupRef} className={`ambient-mesh${pipeline ? ' ambient-pipeline' : ''}`} aria-hidden="true">
      <defs>
        <linearGradient id={`${id}-silver`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#abb9c3" stopOpacity="0.38" />
          <stop offset="0.48" stopColor="#78868f" stopOpacity="0.13" />
          <stop offset="1" stopColor="#272e34" stopOpacity="0.22" />
        </linearGradient>
        <linearGradient id={`${id}-reflection`} x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#d1dee6" stopOpacity="0.23" />
          <stop offset="1" stopColor="#272e34" stopOpacity="0.06" />
        </linearGradient>
        <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%" colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation="2.8" />
        </filter>
        <radialGradient id={`${id}-vertex`}>
          <stop offset="0" stopColor="#e7f1f6" stopOpacity="0.55" />
          <stop offset="0.23" stopColor="#d6e5ed" stopOpacity="0.16" />
          <stop offset="1" stopColor="#d6e5ed" stopOpacity="0" />
        </radialGradient>
      </defs>
      {mesh.faces.map((face, i) => (
        <polygon key={i} className="ambient-plane" points={polygon(mesh.points, face)}
          fill={`url(#${id}-${i % 4 === 0 ? 'reflection' : 'silver'})`}
          opacity={pipeline ? (i % 3 === 0 ? 0.62 : 0.32) : 0.45 + Math.max(0, face.reduce((sum, v) => sum + (mesh.points[v].z ?? 0), 0) / 3) * 0.5} />
      ))}
      <path className="ambient-edges ambient-edge-glow" d={edgePath(mesh.points, edges)} filter={`url(#${id}-glow)`} />
      <path className="ambient-edges ambient-edge-line" d={edgePath(mesh.points, edges)} />
      {mesh.points.map((point, i) => (
        <g key={i}>
          <circle data-vertex={i} cx={point.x} cy={point.y} r={pipeline ? 7 : 11} fill={`url(#${id}-vertex)`} />
          <circle data-vertex={i} cx={point.x} cy={point.y} r={pipeline ? 0.9 : 1.15} fill="#e7f1f6" opacity={pipeline ? 0.44 : 0.65} />
        </g>
      ))}
    </g>
  );
}

// Different closed solids, held at fixed poses in the same silver/graphite
// material. Shared vertices keep faces joined as the junctions drift.
function projectSolid(vertices: Vertex[], faces: Face[], yaw: number, tilt: number, seed: number, tips: number[] = []): Mesh {
  const points = vertices.map(([x, y, z]) => {
    const rx = x * Math.cos(yaw) + z * Math.sin(yaw);
    const rz = -x * Math.sin(yaw) + z * Math.cos(yaw);
    const ry = y * Math.cos(tilt) - rz * Math.sin(tilt);
    const depth = y * Math.sin(tilt) + rz * Math.cos(tilt);
    const perspective = 4.7 / (4.7 - depth);
    return { x: 300 + rx * 205 * perspective, y: 300 + ry * 205 * perspective, z: depth };
  });
  faces.sort((a, b) => a.reduce((sum, i) => sum + (points[i].z ?? 0), 0) - b.reduce((sum, i) => sum + (points[i].z ?? 0), 0));
  return {
    points, faces, seed,
    amplitudes: points.map((_, i) => tips.includes(i) ? 3 : 6.5 + variation(seed + i) * 2),
  };
}

function shard(): Mesh {
  const vertices: Vertex[] = [[0.08, -1.35, 0.02]];
  for (let i = 0; i < 6; i++) {
    const a = i / 6 * Math.PI * 2;
    vertices.push([Math.cos(a) * 0.73, -0.42 + (i % 2) * 0.055, Math.sin(a) * 0.73]);
  }
  for (let i = 0; i < 6; i++) {
    const a = i / 6 * Math.PI * 2 + 0.17;
    vertices.push([Math.cos(a) * 0.81, 0.46 - (i % 2) * 0.04, Math.sin(a) * 0.81]);
  }
  vertices.push([-0.06, 1.32, -0.04]);
  const faces: Face[] = [];
  for (let i = 0; i < 6; i++) {
    const n = (i + 1) % 6;
    faces.push([0, 1 + i, 1 + n], [1 + i, 7 + i, 1 + n], [1 + n, 7 + i, 7 + n], [13, 7 + n, 7 + i]);
  }
  return projectSolid(vertices, faces, 0.6, -0.18, 13, [0, 13]);
}

function obliquePrism(): Mesh {
  const vertices: Vertex[] = [];
  for (const upper of [true, false]) {
    for (let i = 0; i < 5; i++) {
      const a = i / 5 * Math.PI * 2 + 0.18;
      const x = Math.cos(a) * (upper ? 0.82 : 1.02);
      const z = Math.sin(a) * (upper ? 0.82 : 1.02);
      vertices.push([x + (upper ? -0.2 : 0.12), (upper ? -0.82 : 0.78) + x * 0.22, z]);
    }
  }
  vertices.push([-0.2, -0.82, 0], [0.12, 0.78, 0]);
  const faces: Face[] = [];
  for (let i = 0; i < 5; i++) {
    const n = (i + 1) % 5;
    faces.push([10, n, i], [11, 5 + i, 5 + n], [i, n, 5 + i], [n, 5 + n, 5 + i]);
  }
  return projectSolid(vertices, faces, 0.65, -0.35, 47);
}

function octahedron(): Mesh {
  const vertices: Vertex[] = [
    [-0.18, -1.2, 0.14], [0.2, 1.06, -0.1],
    [-1.02, -0.02, 0], [0.06, 0.08, 0.94], [1.08, 0.13, 0], [-0.03, -0.12, -0.86],
  ];
  const faces: Face[] = [];
  for (let i = 0; i < 4; i++) {
    const a = 2 + i, b = 2 + (i + 1) % 4;
    faces.push([0, a, b], [1, b, a]);
  }
  return projectSolid(vertices, faces, -0.35, -0.2, 83, [0, 1]);
}

function polyhedron(): Mesh {
  const phi = (1 + Math.sqrt(5)) / 2;
  const vertices: Vertex[] = [
    [-1, phi, 0], [1, phi, 0], [-1, -phi, 0], [1, -phi, 0],
    [0, -1, phi], [0, 1, phi], [0, -1, -phi], [0, 1, -phi],
    [phi, 0, -1], [phi, 0, 1], [-phi, 0, -1], [-phi, 0, 1],
  ].map(([x, y, z]) => [x * 0.72 + y * 0.08, y * 0.66, z * 0.62]);
  const faces: Face[] = [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
  ];
  return projectSolid(vertices, faces, 0.42, 0.28, 127);
}

const crystals = {
  maverick: shard(),
  pilot: obliquePrism(),
  founder: octahedron(),
  contact: polyhedron(),
};

export function FacetedBackdrop({ className, variant }: { className: string; variant: keyof typeof crystals }) {
  return (
    <div className={`ambient-backdrop ${className}`} aria-hidden="true">
      <svg className="ambient-svg" viewBox="0 0 600 600" focusable="false">
        <AmbientMesh mesh={crystals[variant]} />
      </svg>
    </div>
  );
}

// All eleven silhouette points are unchanged from the approved pipeline.
// Interior hubs supply the motion; the perimeter, nodes and arrows stay fixed.
const pipelinePoints = [
  [90, 225], [335, 80], [510, 225], [705, 80], [890, 225], [1080, 80],
  [1150, 260], [930, 338], [720, 286], [500, 348], [260, 294],
  [340, 235], [665, 235], [1000, 245],
].map(([x, y]) => ({ x, y }));
const pipelineFaces: Face[] = [];
for (const [hub, ring] of [[11, [0, 1, 2, 9, 10]], [12, [2, 3, 4, 8, 9]], [13, [4, 5, 6, 7, 8]]] as const) {
  for (let i = 0; i < ring.length; i++) pipelineFaces.push([hub, ring[i], ring[(i + 1) % ring.length]]);
}
const pipelineMesh: Mesh = {
  points: pipelinePoints,
  faces: pipelineFaces,
  amplitudes: pipelinePoints.map((_, i) => i < 11 ? 0 : 5.5),
  seed: 173,
};

export function PipelineFacets() {
  return <AmbientMesh mesh={pipelineMesh} pipeline />;
}
